import type { FC, ReactNode } from 'react'
import type { PersonSummary } from '@/types'

import Link from 'next/link'
import { cn } from '@/lib/utils'

import PersonAvatar from '@/components/family/person-avatar'
import LifeDates from '@/components/family/life-dates'

interface PersonCardProps {
    person: PersonSummary
    highlight?: boolean
    className?: string
}

const PersonCard: FC<PersonCardProps> = ({ person, highlight = false, className }): ReactNode => {
    return (
        <Link
            href={`/people/${person.slug}`}
            className={cn(
                'group flex items-center gap-4 rounded-2xl border bg-linen p-4 text-left shadow-soft transition-[translate,box-shadow,border-color] duration-300',
                'hover:-translate-y-0.5 hover:border-forest/40 hover:shadow-lifted',
                highlight ? 'border-gold ring-2 ring-gold/25' : 'border-line',
                className
            )}
        >
            <PersonAvatar person={person} size='md' />

            <span className='min-w-0'>
                {person.relation && (
                    <span
                        data-mdlfy-cms={`people:${person.id}:relation`}
                        className='mb-0.5 block text-xs font-semibold tracking-[0.12em] text-clay uppercase'
                    >
                        {person.relation}
                    </span>
                )}

                <span
                    data-mdlfy-cms={`people:${person.id}:name`}
                    className='block font-heading text-lg leading-snug font-semibold text-ink transition-colors duration-200 group-hover:text-forest'
                >
                    {person.name}
                </span>

                <LifeDates person={person} className='mt-1' />
            </span>
        </Link>
    )
}

export default PersonCard
